import { CreditCardContainer } from "../Components";
import { useCallback, useState } from "react";

import { Badge, Box, DropdownMenu } from "@adfenix/alexandria";
import { CardBrand } from "../DeleteStripeCard/CardBrand";

import { RemoveCardModal } from "../DeleteStripeCard/RemoveCardModal";
import { useMutation } from "@tanstack/react-query";
import type { StripeService } from "@adfenix/types";

import UilEllipsis from "@iconscout/react-unicons/icons/uil-ellipsis-h";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  changeOfficeDefaultStripeCard,
  removeOfficeStripeCard
} from "api/services/stripeService";

interface OfficeRemoveStripeCardProps {
  cardInfo: StripeService.CustomerCardPaymentMethodsDescription;
  officeId: string;
  onRemove: () => void;
  onDefaultCardChange?: () => void;
  viewCardsMode?: boolean;
}

export const OfficeRemoveOrChangeStripeCard = ({
  cardInfo,
  officeId,
  onRemove,
  onDefaultCardChange,
  viewCardsMode
}: OfficeRemoveStripeCardProps) => {
  const { t } = useTranslation();

  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  const closeRemoveCardModal = () => {
    setRemoveModalOpen(false);
  };

  const apiMethodHqOrOfficeCreditCard = useCallback(
    () =>
      removeOfficeStripeCard({
        subjectId: officeId!,
        paymentMethodId: cardInfo.paymentMethodId
      }),
    [cardInfo, officeId]
  );

  const { mutateAsync, isLoading, isError } = useMutation(
    apiMethodHqOrOfficeCreditCard
  );

  const { mutateAsync: changeDefaultStripeCard } = useMutation(
    changeOfficeDefaultStripeCard
  );

  const handleChangeCard = async () => {
    try {
      await changeDefaultStripeCard({
        subjectId: officeId,
        paymentMethodId: cardInfo.paymentMethodId
      });

      onDefaultCardChange && onDefaultCardChange();
    } catch (error) {
      toast.error(t("billing_failed_to_change_default_card"));
    }
  };
  return (
    <>
      <RemoveCardModal
        open={removeModalOpen}
        onClose={closeRemoveCardModal}
        onRemove={async () => {
          try {
            await mutateAsync();
            setRemoveModalOpen(false);
            onRemove();
          } catch {
            console.error(t("billing_failed_to_remove_card"));
          }
        }}
        loading={isLoading}
        error={isError}
      />

      <Box
        display="flex"
        alignItems="center"
        style={{
          gap: "12px",
          flex: "1"
        }}
      >
        <CreditCardContainer
          style={{
            gap: "32px",
            flex: "1 1 100%"
          }}
        >
          <Box display="flex" alignItems="center">
            <CardBrand brand={cardInfo?.cardBrand} />
            <Box pX="xs">
              <span>•••• •••• •••• {cardInfo?.cardLast4}</span>
            </Box>
          </Box>
          <span>
            {cardInfo?.cardExpMonth} / {cardInfo?.cardExpYear}
          </span>

          {cardInfo?.isDefault ? (
            <Badge color="success" lightness="darker">
              {t("billing_active_credit_card_label")}
            </Badge>
          ) : (
            <Badge color="grey" lightness="darker">
              {t("billing_inactive_credit_card_label")}
            </Badge>
          )}
        </CreditCardContainer>

        {cardInfo?.isDefault ? null : (
          <DropdownMenu
            logLabelSuffix="office-card-actions"
            items={{
              Actions: [
                {
                  icon: "check",
                  callback: () => handleChangeCard(),
                  text: t("billing_use_this_card_label"),

                  shouldHide: viewCardsMode
                },
                {
                  icon: "trash-alt",
                  callback: () => setRemoveModalOpen(true),
                  text: t("billing_remove_this_card_label")
                }
              ]
            }}
          >
            <UilEllipsis color="#111" size="20px" />
          </DropdownMenu>
        )}
      </Box>
    </>
  );
};
